import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Grid,
  Zoom,
  Tooltip,
  Paper,
  Button,
  Pagination,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import test from "../../assets/images/test.png";
import assessment from "../../assets/images/method.png";
import exam from "../../assets/images/exam.png";
import score from "../../assets/images/speedometer.png";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { TableNodata } from "../../components/TableNodata";
import { OverAllLoader } from "../../components/OverAllLoader";
import UpcomingProctoredAssessments from "./UpcomingProctoredAssessments";
import TopPerformers from "./TopPerformers";
import { useSnackbar } from "../../providers/SnackbarProvider";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const InstituteAdminDashboard = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [loaded, setLoaded] = useState(false);
  const currentMonth = dayjs().month(); // 0-11
  const currentYear = dayjs().year();

  const [selectedMonth] = useState(currentMonth);
  const [selectedYear] = useState(currentYear);
  const [count, setCount] = useState(0);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 10
  );
  const [searchKey, setSearchKey] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const [testList, setTestList] = useState();
  const [overLoading, setOverLoading] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();



  useEffect(() => {
    // Trigger animation once the component mounts
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const commonAvatarStyle = {
    bgcolor: "#e8f2ff", // light blue background
    color: "#1e88e5", // blue text/icon color
    width: 24,
    height: 24,
    fontWeight: "bold",
    border: "2px solid #bbdefb",
  };


  const trainerList = async () => {
    setLoading(true);
    try {
      const payload = {
        page: page,
        size: rowsPerPage,
        search_key: searchKey,
        institute_id: null,
      };
      const res = await api.post("ListTrainers", payload);
      if (res?.data?.status === "success") {
        setListData(res?.data?.data);
        setCount(res?.data?.count);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log("Error :", error);
      setLoading(false);
    }
  };

  const testCount = async () => {
    setOverLoading(true);
    try {
      const res = await api.post("GetAllAssessmentsCountForInstituteAdmin", {
        institute_id: +localStorage.getItem("InstituteId"),
        month: selectedMonth + 1,
        year: selectedYear,
      });
      if (res?.data?.status === "success") {
        setTestList(res?.data);
      }
    } catch (error) {
      console.log("Error : ", error);
      showSnackbar("error", "Failed to fetch assessment count.");
    } finally {
      setOverLoading(false);
    }
  };

  useEffect(() => {
    testCount();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    trainerList();
  }, [page, rowsPerPage, searchKey]);

  // Check login status
  const isLoggedIn = localStorage.getItem("logIn") !== "true";

  // Function to wrap content with Zoom conditionally
  // useCallback keeps the same component between renders; otherwise every state change
  // remounts the wrapped cards (the Upcoming card would refetch and reset its filter)
  // `in` comes from the props (every usage passes in={loaded}), so `loaded` flipping true
  // doesn't create a new component and remount the cards (the Upcoming card fetched twice).
  const ConditionalZoom = useCallback(({ children, style, key, in: inProp }) => {
    if (isLoggedIn) {
      return (
        <Zoom in={inProp} style={style} key={key}>
          {children}
        </Zoom>
      );
    }
    return children;
  }, [isLoggedIn]);

  return (
    <Box>
      {overLoading ? (
        <OverAllLoader message="Fetching dashboard..." />
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid>
              {/* Header Section */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: "#0a0a0a" }}
                >
                  Dashboard
                </Typography>
              </Box>

              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Overall summary of exam activities and students' performance
                outcomes.
              </Typography>
            </Grid>
          </Box>

          {/* Cards Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            {/* Total Tests Card */}
            <ConditionalZoom
              in={loaded}
              style={{ transitionDelay: "400ms" }}
              key="total-tests-card"
            >
              <Card
                sx={{
                  flex: {
                    xs: "1 1 100%",
                    sm: "1 1 calc(40% - 16px)",
                    md: "1 1 calc(21% - 16px)",
                  },
                  borderRadius: "8px",
                  border: "1px solid #c1c1c6ff",
                  background: "#fff",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  p: 2,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.03)",
                    boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                    background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                  },
                }}
                onClick={() =>
                  navigate("/questionPaperBuilder")
                }
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: "#333", lineHeight: 1.1 }}
                  >
                    No. of {getLabel('QUESTION_PAPER')}
                  </Typography>
                  <Avatar sx={commonAvatarStyle}>
                    <Box
                      component="img"
                      src={test}
                      alt={`No. of ${getLabel('EXAM')} Taken`}
                      sx={{ width: 26, height: 26 }}
                    />
                  </Avatar>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: "#1e88e5", mt: 1 }}
                >
                  {testList?.total_assessments || "0"}
                </Typography>
              </Card>
            </ConditionalZoom>

            {/* Self Assessments Card */}
            <ConditionalZoom
              in={loaded}
              style={{ transitionDelay: "400ms" }}
              key="self-assessments-card"
            >
              <Card
                sx={{
                  flex: {
                    xs: "1 1 100%",
                    sm: "1 1 calc(40% - 16px)",
                    md: "1 1 calc(21% - 16px)",
                  },
                  borderRadius: "8px",
                  border: "1px solid #c1c1c6ff",
                  background: "#fff",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  p: 2,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.03)",
                    boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                    background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                  },
                }}
                onClick={() =>
                  navigate("/questionPaperBuilder", {
                    state: {
                      testType: "Self Assessment",
                      testTypeId: 1,
                    },
                  })
                }
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: "#333", lineHeight: 1.1 }}
                  >
                    No. of Self Assessments
                  </Typography>
                  <Avatar sx={commonAvatarStyle}>
                    <Box
                      component="img"
                      src={assessment}
                      alt="Self Assessments"
                      sx={{ width: 26, height: 26 }}
                    />
                  </Avatar>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: "#1e88e5", mt: 1 }}
                >
                  {testList?.total_self_assessments || "0"}
                </Typography>
              </Card>
            </ConditionalZoom>

            {/* Proctored Tests Card */}
            <ConditionalZoom
              in={loaded}
              style={{ transitionDelay: "400ms" }}
              key="proctored-tests-card"
            >
              <Card
                sx={{
                  flex: {
                    xs: "1 1 100%",
                    sm: "1 1 calc(40% - 16px)",
                    md: "1 1 calc(21% - 16px)",
                  },
                  borderRadius: "8px",
                  border: "1px solid #c1c1c6ff",
                  background: "#fff",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  p: 2,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.03)",
                    boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                    background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                  },
                }}
                onClick={() =>
                  navigate("/questionPaperBuilder", {
                    state: {
                      testType: "Proctored Assessment",
                      testTypeId: 2,
                    },
                  })
                }
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: "#333", lineHeight: 1.1 }}
                  >
                    No. of Proctored Assessments
                  </Typography>
                  <Avatar sx={commonAvatarStyle}>
                    <Box
                      component="img"
                      src={exam}
                      alt="Proctored Tests"
                      sx={{ width: 26, height: 26 }}
                    />
                  </Avatar>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: "#1e88e5", mt: 1 }}
                >
                  {testList?.total_proctored_assessments || "0"}
                </Typography>
              </Card>
            </ConditionalZoom>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
              border: "1px solid #c1c1c6ff",
              background: "#fff",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
              p: 2
            }}
          >
            <ConditionalZoom
              in={loaded}
              style={{ transitionDelay: "600ms" }}
              key="performance-overview-header"
            >
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Grid
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700}>
                    {getLabel('INSTITUTE')} performance Overview
                  </Typography>
                </Grid>

              </Grid>
            </ConditionalZoom>

            <Box sx={{ display: "flex", gap: 3 }}>
              {/* Top Performers Card */}
              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="top-performers-card"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TopPerformers showTestTypeFilter />
                </Box>
              </ConditionalZoom>

              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="upcoming-proctored-assessments-card"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Own institute (from the login token), All / Proctored / Self Assessment */}
                  <UpcomingProctoredAssessments showTestTypeFilter />
                </Box>
              </ConditionalZoom>
            </Box>
          </Box>

          {/* Trainer Overview Section */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "1000ms" }}
            key="trainer-overview-section"
          >
            <Box
              sx={{
                mt: 3,
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                p: 3,
                overflowX: "auto",
              }}
            >
              {/* Header */}
              <Grid
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  {getLabel('TRAINER')} Overview
                </Typography>
              </Grid>

              {/* Table Header */}
              <Grid
                container
                sx={{
                  py: 1.5,
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: 600,
                  color: "text.secondary",
                  flexWrap: "nowrap",
                  minWidth: 820,
                }}
              >
                <Grid item xs={2} sx={{ width: "22%" }}>
                  {getLabel('TRAINER')}
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  Phone
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  Gender
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  Date of Birth
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  {getLabel('QUESTION_PAPER')}
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  {getLabel('STUDENT')}
                </Grid>
                <Grid item xs={2} sx={{ width: "13%" }}>
                  Status
                </Grid>
              </Grid>

              {/* Trainer Data */}
              {listData.map((trainer, index) => (
                <Grid
                  container
                  key={`trainer-${index}`}
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    borderBottom: "1px solid #f2f2f2",
                    "&:hover": { backgroundColor: "#fafafa" },
                    flexWrap: "nowrap",
                    minWidth: 820,
                  }}
                >
                  {/* Trainer Info */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "22%",
                    }}
                  >
                    <Avatar sx={commonAvatarStyle}>
                      {trainer?.first_name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>
                        {trainer.first_name} {trainer.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {trainer.email}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={2} sx={{ width: "13%" }}>
                    <Typography variant="body2" color="text.secondary">
                      {trainer.phone || "-"}
                    </Typography>
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={2} sx={{ width: "13%" }}>
                    <Typography variant="body2" color="text.secondary">
                      {trainer.gender || "-"}
                    </Typography>
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={2} sx={{ width: "13%" }}>
                    <Typography variant="body2" color="text.secondary">
                      {trainer.dob ? dayjs(trainer.dob).format("DD-MM-YYYY") : "-"}
                    </Typography>
                  </Grid>

                  {/* Assessments */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "13%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate("/questionPaperBuilder", {
                          state: {
                            trainerId: trainer?.user_id,
                            TrainerName:
                              trainer?.first_name + " " + trainer?.last_name,
                          },
                        })
                      }
                    >
                      <DescriptionOutlinedIcon
                        sx={{ color: "#1e88e5", fontSize: 20 }}
                      />
                      <Typography fontWeight={500}>
                        {trainer.total_assessments || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Students */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "13%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                      }}
                      onClick={() => (
                        localStorage.setItem("prevScreenDashboard", true),
                        navigate("/students", {
                          state: {
                            userId: trainer?.user_id,
                            dashboard: true,
                            TrainerName:
                              trainer?.first_name + " " + trainer?.last_name,
                          },
                        })
                      )}
                    >
                      <GroupOutlinedIcon
                        sx={{ color: "#1e88e5", fontSize: 20 }}
                      />
                      <Typography fontWeight={500}>
                        {trainer.total_students || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Status */}
                  <Grid item xs={2} sx={{ width: "13%" }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 0.5,
                        borderRadius: "12px",
                        fontWeight: "bold",
                        fontSize: "12px",
                        backgroundColor:
                          trainer.status === "ACTIVE"
                            ? "#d4edda"
                            : trainer.status === "INACTIVE"
                              ? "#f8d7da"
                              : "#f1f1f1",
                        color:
                          trainer.status === "ACTIVE"
                            ? "#155724"
                            : trainer.status === "INACTIVE"
                              ? "#721c24"
                              : "#333",
                        textAlign: "center",
                      }}
                    >
                      {trainer.status}
                    </Box>
                  </Grid>
                </Grid>
              ))}

              {listData.length === 0 && !loading && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <TableNodata />
                </Box>
              )}

              {loading && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography>Loading trainers...</Typography>
                </Box>
              )}

              {((page === 0 && listData?.length > rowsPerPage - 1) ||
                page !== 0) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    {page !== 0 ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={
                            <ArrowBackIcon
                              sx={{
                                color: page === 0 ? "#a2a2a2" : "#414651",
                              }}
                            />
                          }
                          disabled={page === 0}
                          onClick={() => setPage(page - 1)}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            color: page === 0 ? "#a2a2a2" : "#414651",
                            borderColor: "#d0d0d0",
                          }}
                        >
                          Previous
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ width: "110px" }} />
                    )}

                    <Pagination
                      page={page + 1}
                      onChange={(e, value) => setPage(value - 1)}
                      count={Math.ceil(count / rowsPerPage)}
                      siblingCount={3}
                      boundaryCount={3}
                      shape="rounded"
                      hidePrevButton
                      hideNextButton
                    />
                    {page + 1 !== Math.ceil(count / rowsPerPage) ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          variant="outlined"
                          endIcon={
                            <ArrowForwardIcon
                              sx={{
                                color:
                                  page + 1 === count ? "#a2a2a2" : "#414651",
                              }}
                            />
                          }
                          disabled={page + 1 === count}
                          onClick={() => setPage(page + 1)}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            color: page + 1 === count ? "#a2a2a2" : "#414651",
                            borderColor: "#d0d0d0",
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ width: "110px" }} />
                    )}
                  </Box>
                )}
            </Box>
          </ConditionalZoom>
        </Box>
      )}
    </Box>
  );
};

export default InstituteAdminDashboard;